import { useEffect } from 'react';
import { useRouter } from 'next/compat/router';
import { useObserver } from 'mobx-react-lite';
import { useFlags } from 'launchdarkly-react-client-sdk';
import _ from 'lodash';

import { IrlEventTrackingEvent } from 'src/gql/graphql';
import useUser from 'hooks/use-user';
import type { AuthOptions } from './use-irl-event-auth';

// Default fallback in minutes
const DEFAULT_ACCOUNT_AGE_THRESHOLD_MINUTES = 5;

type UseSocialAuthTrackingParams = {
  handleAuthSuccess: (options: AuthOptions) => Promise<void>;
};

/**
 * Determines if a user is newly created based on their createdAt timestamp.
 * Returns true if user was created within the specified time window (in minutes).
 */
export function checkNewUser(createdAt: string | null, windowMinutes: number): boolean {
  if (!createdAt) {
    return false;
  }
  const windowMs = windowMinutes * 60 * 1000;
  return Date.now() - new Date(createdAt).getTime() < windowMs;
}

/**
 * Detects when a user returns from social auth redirect and handles post-auth flow.
 * Uses URL state (authSource=social) as the guard - removing it after handling prevents duplicates.
 * Determines if user is new (signup) or existing (login) based on account age.
 * Callback from useIrlEventFlow that creates DPOS customer and tracks auth event.
 */
export function useSocialAuthTracking({ handleAuthSuccess }: UseSocialAuthTrackingParams): void {
  const router = useRouter();
  const user = useUser();
  const flags = useFlags();

  const ecommUserId = useObserver(() => user.id) ?? '';
  const userCreatedAt = useObserver(() => user.createdAt);

  const thresholdMinutes =
    (flags['ecomm.irl-events.account-age-threshold-minutes.config'] as number | undefined) ??
    DEFAULT_ACCOUNT_AGE_THRESHOLD_MINUTES;

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- router can be null
    if (!router?.isReady || router?.query?.authSource !== 'social' || !ecommUserId) {
      return;
    }

    const isNewUser = checkNewUser(userCreatedAt, thresholdMinutes);
    const authEvent = isNewUser ? IrlEventTrackingEvent.signup : IrlEventTrackingEvent.login;

    void handleAuthSuccess({ authEvent, skipNavigation: true });

    const remainingQuery = _.omit(router.query, 'authSource');
    void router.replace({ pathname: router.pathname, query: remainingQuery }, undefined, { shallow: true });
  }, [router, handleAuthSuccess, ecommUserId, userCreatedAt, thresholdMinutes]);
}
